import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Card, Table, Button, Row, Col, Space, Typography, Modal, Input, Upload, message, Popconfirm, Tag, Select,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, PlayCircleOutlined, ReloadOutlined, InboxOutlined,
  SoundOutlined, EditOutlined, DragOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../services/api';

const { Text, Title } = Typography;
const { Dragger } = Upload;

function fmtSize(bytes) {
  if (!bytes) return '—';
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

// Row draggable com HTML5 DnD nativo — evita adicionar dep de react-dnd.
const DraggableRow = ({ record, index, moveRow, ...restProps }) => {
  const ref = useRef(null);
  const onDragStart = (e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    ref.current?.classList.add('drag-active');
  };
  const onDragEnd = () => ref.current?.classList.remove('drag-active');
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = (e) => {
    e.preventDefault();
    const from = Number(e.dataTransfer.getData('text/plain'));
    if (Number.isNaN(from) || from === index) return;
    moveRow(from, index);
  };
  return (
    <tr
      ref={ref}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={{ cursor: 'move' }}
      {...restProps}
    />
  );
};

const Audios = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = novo
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/audios');
      setRows(r.data.audios || []);
    } catch (err) {
      message.error('Erro ao carregar áudios');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reset = () => {
    setName('');
    setDescription('');
    setTags([]);
    setFile(null);
    setEditingId(null);
  };

  const openNew = () => { reset(); setModalOpen(true); };
  const openEdit = (row) => {
    setEditingId(row.id);
    setName(row.name || '');
    setDescription(row.description || '');
    setTags(Array.isArray(row.tags) ? row.tags : []);
    setFile(null);
    setModalOpen(true);
  };

  const save = async () => {
    if (!name.trim()) { message.error('Nome é obrigatório'); return; }
    if (!editingId && !file) { message.error('Selecione o arquivo'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', name.trim());
      fd.append('description', description.trim());
      fd.append('tags', JSON.stringify(tags));
      if (file) fd.append('file', file);
      if (editingId) {
        await api.patch(`/audios/${editingId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        message.success('Áudio atualizado');
      } else {
        await api.post('/audios', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        message.success('Áudio cadastrado');
      }
      reset();
      setModalOpen(false);
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/audios/${id}`);
      message.success('Áudio removido');
      load();
    } catch (err) {
      message.error('Erro ao remover');
    }
  };

  const playAudio = async (id) => {
    try {
      const res = await api.get(`/audios/${id}/download`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      Modal.info({
        title: 'Reproduzir áudio',
        width: 500,
        content: (
          <audio controls autoPlay style={{ width: '100%' }}>
            <source src={blobUrl} />
          </audio>
        ),
        okText: 'Fechar',
        afterClose: () => URL.revokeObjectURL(blobUrl),
      });
    } catch (err) {
      message.error('Erro ao carregar áudio');
    }
  };

  const moveRow = async (from, to) => {
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setRows(next); // otimista
    try {
      await api.post('/audios/reorder', { order: next.map((x) => x.id) });
    } catch (err) {
      message.error('Erro ao reordenar');
      load(); // reverte
    }
  };

  const columns = [
    {
      title: '',
      key: 'drag',
      width: 40,
      render: () => <DragOutlined style={{ color: '#999', cursor: 'move' }} />,
    },
    {
      title: 'Nome',
      dataIndex: 'name',
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong><SoundOutlined /> {v}</Text>
          {r.description && <Text type="secondary" style={{ fontSize: 12 }}>{r.description}</Text>}
          {Array.isArray(r.tags) && r.tags.length > 0 && (
            <Space size={4} wrap>
              {r.tags.map(t => <Tag key={t} color="purple" style={{ fontSize: 10 }}>{t}</Tag>)}
            </Space>
          )}
        </Space>
      ),
    },
    {
      title: 'Arquivo',
      dataIndex: 'original_name',
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>{v || '—'}</Text>
          <Space size={4}>
            <Tag color="blue" style={{ fontSize: 10 }}>{r.mime_type}</Tag>
            <Text type="secondary" style={{ fontSize: 11 }}>{fmtSize(r.size_bytes)}</Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Cadastrado por',
      dataIndex: 'created_by_username',
      width: 150,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Data',
      dataIndex: 'created_at',
      width: 180,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>{dayjs(v).format('DD/MM/YYYY HH:mm')}</Text>
          {r.updated_at && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              editado {dayjs(r.updated_at).fromNow ? dayjs(r.updated_at).fromNow() : dayjs(r.updated_at).format('DD/MM HH:mm')}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 220,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<PlayCircleOutlined />} onClick={() => playAudio(r.id)}>Ouvir</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Editar</Button>
          <Popconfirm title="Remover áudio?" onConfirm={() => remove(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <SoundOutlined /> Áudios
          </Title>
          <Text type="secondary">Cadastre áudios pros organizadores. Arraste as linhas pra ordenar.</Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Atualizar</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>
              Cadastrar áudio
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          columns={columns}
          pagination={false}
          components={{
            body: {
              row: DraggableRow,
            },
          }}
          onRow={(record, index) => ({ record, index, moveRow })}
        />
      </Card>

      <Modal
        open={modalOpen}
        onCancel={() => { reset(); setModalOpen(false); }}
        title={editingId ? `Editar áudio #${editingId}` : 'Cadastrar novo áudio'}
        onOk={save}
        confirmLoading={saving}
        okText={editingId ? 'Salvar' : 'Enviar'}
        cancelText="Cancelar"
        width={550}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text>Nome *</Text>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Aviso 1ª chamada" />
          </div>
          <div>
            <Text>Descrição</Text>
            <Input.TextArea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Descrição opcional" />
          </div>
          <div>
            <Text>Tags</Text>
            <Select
              mode="tags"
              style={{ width: '100%' }}
              value={tags}
              onChange={setTags}
              placeholder="Ex: welcome, treinamento (Enter pra adicionar)"
              tokenSeparators={[',']}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Use tags pra agrupar áudios (ex: <code>welcome</code> = enviado no cadastro do organizador).
            </Text>
          </div>
          <div>
            <Text>{editingId ? 'Trocar arquivo (opcional)' : 'Arquivo (mp3 ou ogg, máx 25MB) *'}</Text>
            <Dragger
              accept=".mp3,.ogg,audio/mpeg,audio/ogg"
              beforeUpload={(f) => { setFile(f); return false; }}
              maxCount={1}
              fileList={file ? [{ uid: '1', name: file.name, size: file.size }] : []}
              onRemove={() => setFile(null)}
            >
              <p><InboxOutlined /></p>
              <p>{editingId ? 'Arraste pra trocar o arquivo atual' : 'Arraste o arquivo ou clique pra selecionar'}</p>
            </Dragger>
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default Audios;
